// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Utils
import { getEMRErrorMessage } from "src/utils/emr/emrErrorHelper";

// Types and API
import { EMRPacServerJobPayload } from "../../types";
import { createEMRPacServer } from "../../api";

type CreateEMRPacServerPayload = {
  clinicId: string;
  name: string;
  deviceKey: string;
  jobs: Array<EMRPacServerJobPayload>;
};

export const useCreateEMRPacServer = (
  clinicId: string | undefined,
  hubId: string | undefined
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (payload: CreateEMRPacServerPayload) => {
      if (!clinicId || !hubId) throw new Error();
      const token = await getToken();
      return createEMRPacServer(token, clinicId, hubId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["emr-pac-servers", clinicId, hubId],
      });
      queryClient.invalidateQueries({
        queryKey: ["emr-hub-connections", clinicId, hubId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("PAC server created successfully!"),
      });
    },
    onError: (error) => {
      setSnackbarInfo({
        variant: "error",
        message: getEMRErrorMessage(
          error,
          t("PAC server could not be created!"),
          t
        ),
      });
    },
  });
};
